import {
  Attribute as Gw2Attribute,
  Augmentation as Gw2Augmentation,
  Boon as Gw2Boon,
  CommonEffect as Gw2CommonEffect,
  Condition as Gw2Condition,
  ControlEffect as Gw2ControlEffect,
  DetailsHeader,
  Icon,
  Item as Gw2Item,
  MistlockInstability as Gw2MistlockInstability,
  Profession,
  Skill as Gw2Skill,
  Tooltip,
  Trait as Gw2Trait,
} from '@discretize/gw2-ui-new';
import { iconSizes } from '@discretize/react-discretize-components';
import { capitalize } from '@mui/material';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { withGw2Theme } from '../../utils/withGw2Theme';
import Gw2Component from './Gw2Component';

const useStyles = makeStyles()((theme) => ({
  root: {
    [theme.breakpoints.up('lg')]: {
      whiteSpace: 'nowrap',
    },
  },
}));

const StyledGw2Component = withGw2Theme()(
  ({ id, className, size, name, Component, ...rest }) => {
    const { classes } = useStyles();
    return (
      <Component
        className={classNames(classes.root, className)}
        style={{
          fontSize: iconSizes[size],
          lineHeight: 0,
        }}
        {...(id && { id: parseInt(id, 10) })}
        {...(name && { name: capitalize(name) })}
        {...rest}
      />
    );
  },
);

export const NoSelection = React.memo(({ className, size, ...rest }) => {
  const { classes } = useStyles();

  return (
    <Tooltip content={<DetailsHeader>Empty Slot</DetailsHeader>}>
      <span
        className={classNames(classes.root, className)}
        style={{
          fontSize: iconSizes[size],
          lineHeight: 0,
          fontFamily: 'Menomonia',
        }}
      >
        <Icon name="Empty" {...rest} />
      </span>
    </Tooltip>
  );
});

NoSelection.propTypes = {
  size: PropTypes.string,
};
NoSelection.defaultProps = {
  size: 'large',
};

export const Item = React.memo(({ type, stat, upgrades, ...rest }) => {
  return (
    <StyledGw2Component
      Component={Gw2Item}
      {...(type !== 'Sigil' && type && { type })}
      {...(stat && { stat: capitalize(stat) })}
      {...(upgrades && {
        upgrades:
          typeof upgrades === 'string'
            ? upgrades.split(',').map((up) => {
                return parseInt(
                  up.replaceAll('{[', '').replaceAll(']}', ''),
                  10,
                );
              })
            : upgrades,
      })}
      {...rest}
    />
  );
});

Item.propTypes = {
  stat: PropTypes.string,
  type: PropTypes.string,
};
Item.defaultProps = {
  stat: null,
  type: null,
};

export const Skill = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2Skill} {...rest} />
));

export const Trait = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2Trait} {...rest} />
));

export const Boon = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2Boon} {...rest} />
));

export const Condition = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2Condition} {...rest} />
));

export const Attribute = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2Attribute} {...rest} />
));

export const Augmentation = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2Augmentation} {...rest} />
));

export const ControlEffect = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2ControlEffect} {...rest} />
));

export const CommonEffect = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2CommonEffect} {...rest} />
));

export const MistlockInstability = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2MistlockInstability} {...rest} />
));

export const SpecialActionKey = React.memo(({ ...rest }) => (
  <StyledGw2Component
    Component={Gw2Component}
    type="specialactionkey"
    {...rest}
  />
));

export const Uncategorized = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Gw2Component} type="uncategorized" {...rest} />
));

export const Specialization = React.memo(({ ...rest }) => (
  <StyledGw2Component Component={Profession} {...rest} />
));
